import { DatePipe, LowerCasePipe } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { TextTrackingComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/text-tracking/text-tracking.component';
import {
  IKeywordCard,
  IKpiCard,
} from '@cta-analytics-details/cta-analytics-details-kpis-tile/cta-analytics-details-kpi-card/cta-analytics-details-kpi-card.interface';
import { AnalyticsDetailsTableComponent } from '@cta-analytics-dialog/deliverable-analytics-content/analytics-details-table/analytics-details-table.component';
import { DeliverableFindingsStore } from '@cta-analytics-dialog/deliverable-analytics-content/deliverable-findings.store';
import { LineGraphComponent } from '@cta-analytics-dialog/deliverable-analytics-content/graphs/line-graph/line-graph.component';
import { ILineGraphInputData } from '@cta-analytics-dialog/deliverable-analytics-content/graphs/line-graph/line-graph.interface';
import { EDeliverableType } from '@enums/campaign.enum';
import { ITextTrackingAnalytics } from '@interfaces/deliverable-analytics.interface';
import { TranslateModule } from '@ngx-translate/core';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { EmbeddedVideoPlayerComponent } from '@shared/components/embedded-video-player/embedded-video-player.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { AnalyticsDetailsUtilitiesService } from '@utilities/analytics-details-utilities.service';
import { FindingVisualizerService } from '@utilities/finding-visualizer.service';
import { StreamDetailsDeliverableBaseDirective } from '../../stream-details-deliverable-base.directive';
import { ITextTrackingStreamAnalyticsTableData } from './text-tracking-stream-analytics.interface';

@Component({
  imports: [
    DatePipe,
    TranslateModule,
    LineGraphComponent,
    AnalyticsDetailsTableComponent,
    MatTableModule,
    MatSortModule,
    MatButtonModule,
    EmbeddedVideoPlayerComponent,
    TextTrackingComponent,
    DotsSpinnerComponent,
    IconComponent,
    LowerCasePipe,
    MatMenuModule,
  ],
  providers: [DeliverableFindingsStore],
  selector: 'app-text-tracking-stream-analytics',
  templateUrl: './text-tracking-stream-analytics.component.html',
})
export class TextTrackingStreamAnalyticsComponent extends StreamDetailsDeliverableBaseDirective<
  ITextTrackingStreamAnalyticsTableData,
  ITextTrackingAnalytics
> {
  public override readonly deliverableType = EDeliverableType.textTracking;
  public override readonly kpiCardData: IKpiCard = {
    icon: 'view.svg',
    id: 'textTracking-count',
    title: this.translate.instant('pages.campaignDetails.ctaAnalytics.details.textTracking.totalCount'),
    value: 0,
  };
  public override readonly displayedColumns: string[] = ['timestamp', 'videoTime', 'source', 'title', 'actions'];
  public override readonly dataSource: MatTableDataSource<ITextTrackingStreamAnalyticsTableData> =
    new MatTableDataSource();
  public override readonly currentDeliverable = computed(() => {
    const deliverableId = this.deliverableId();
    const executionAnalytics = this.executionAnalyticsWithFindings();

    if (deliverableId && executionAnalytics) {
      return executionAnalytics?.ctaAnalytics?.textTrackings?.find((tt) => tt.deliverableId === deliverableId);
    }

    return null;
  });

  public readonly keywordKpiList = signal<IKeywordCard[]>([]);
  public readonly keywordKpiCardList = signal<IKpiCard[]>([]);

  public readonly allKpiCards = computed(() => [this.kpiCardData, ...this.keywordKpiCardList()]);

  constructor() {
    super();

    effect(() => {
      if (this.executionAnalyticsWithFindings()) {
        this.setCounts();
        this.setStatus();
        this.setStreamData();
        this.setKeywordList();
      }
    });

    effect(() => {
      this.manualFindingStatusStore?.serverRemovedIds();

      if (this.executionAnalyticsWithFindings() && this.timelineData()) {
        this.prepareChartAndTableData();
      }
    });

    effect(() => {
      const deliverableId = this.deliverableId();
      const executionAnalytics = this.executionAnalyticsWithFindings();

      if (deliverableId && executionAnalytics) {
        const textTracking = this.currentDeliverable();

        this.keywords = textTracking?.keywords?.map((keyword) => keyword.keyword) ?? [];
      }
    });
  }

  private readonly findingVisualizerService = inject(FindingVisualizerService);

  protected override prepareChartAndTableData(): void {
    const dotGraphData: ILineGraphInputData[] = [];
    const keywords = this.currentDeliverable()?.keywords ?? [];
    const timelineData = this.timelineData() ?? [];
    const { sortedDictionary } = this.graphUtilities.processTimelineData(
      timelineData,
      this.stream()?.executionTime,
      this.stream()?.end,
    );
    const removedIds = this.manualFindingStatusStore?.serverRemovedIds() ?? new Set<string>();
    const visualizers = this.findingVisualizerService.buildFromKeywords(keywords, removedIds);

    const tableData: ITextTrackingStreamAnalyticsTableData[] = visualizers.map((visualizer) => {
      const [primaryFinding] = visualizer.findings;

      const kwAnalytics = keywords.find((k) => k.keywordId === primaryFinding.parentId);
      const entryTimestamp = this.dateUtils.parseDateTimeFromApi(primaryFinding.timestamp).getTime();
      const interpolatedViewCount = this.graphUtilities.getInterpolatedViewCount(sortedDictionary, entryTimestamp);

      dotGraphData.push({
        id: visualizer.id,
        status: kwAnalytics?.status ?? primaryFinding.status,
        xAxisValue: entryTimestamp,
        yAxisValue: Math.round(interpolatedViewCount),
      });

      return {
        ...visualizer,
        source: primaryFinding.source,
        text: primaryFinding.text,
        timestamp: primaryFinding.timestamp,
        videoTimeLabel: AnalyticsDetailsUtilitiesService.formatVideoTime(primaryFinding.videoTime),
        videoTimeSeconds: primaryFinding.videoTime ?? 0,
      };
    });

    const sortedTableData = [...tableData].sort(
      (a, b) =>
        this.dateUtils.parseDateTimeFromApi(a.timestamp).getTime() -
        this.dateUtils.parseDateTimeFromApi(b.timestamp).getTime(),
    );

    this.dataSource.data = sortedTableData;
    this.dataCount.set(sortedTableData.length);
    this.dotGraphData.set([...dotGraphData].sort((a, b) => a.xAxisValue - b.xAxisValue));
    this.setKeywordTableRowIds(this.findingVisualizerService.buildKeywordRowIdMap(sortedTableData));
  }

  protected override setCounts(): void {
    const textTracking = this.currentDeliverable();

    const keywords = textTracking?.keywords ?? [];
    const keywordNames = keywords.map((k) => k.keyword);
    const findings = keywords.flatMap((k) => k.findings ?? []);
    const uniqueFindings = AnalyticsDetailsUtilitiesService.getUniqueTextFindings(findings) as string[];

    const counts = AnalyticsDetailsUtilitiesService.getIndividualKeywordCounts(keywordNames, uniqueFindings);
    const total = Object.values(counts).reduce((sum, val) => sum + val, 0);
    this.kpiCardData.value = total;

    AnalyticsDetailsUtilitiesService.mapKeywordCountsToDeliverableForm(this.deliverableForm(), counts);
  }

  protected override setKeywordList(): void {
    const textTracking = this.currentDeliverable();

    if (textTracking?.keywords && textTracking.keywords.length > 0) {
      const { keywordList, kpiCardList } = AnalyticsDetailsUtilitiesService.buildKeywordLists(
        textTracking.keywords,
        'keyword',
        'findings',
      );
      this.keywordKpiList.set(keywordList);
      this.keywordKpiCardList.set(kpiCardList);
    }
  }
}
